import React, { useContext, useState, useMemo } from 'react';
import {
    TableRow,
    TableCell,
    Typography,
    Box,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from 'styled-components';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { AuthContext } from '../../auth/providers/AuthProvider';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { listUsersTemplates, moveDefaultStatementToBill } from '../../../services/template/Templates';

/** Same as bill bar: favorite first, then created_at newest first. */
function sortBillsLikeBillBar(bills, authClaims) {
    const getFavoriteStatus = (bill) => {
        if (bill.owner_sub && authClaims?.user_sub &&
            bill.owner_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
            return bill.favorite || false;
        }
        if (bill.participants?.length && authClaims?.user_sub) {
            const p = bill.participants.find(
                (x) => x.user_sub && x.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()
            );
            if (p && p.favorite !== undefined) return p.favorite;
        }
        return false;
    };
    return [...bills].sort((a, b) => {
        const favA = getFavoriteStatus(a);
        const favB = getFavoriteStatus(b);
        if (favA !== favB) return favA ? -1 : 1;
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
    });
}

/**
 * Inline "Edit statement" form for Default Bill: same table form UX as AddDefaultStatementRow / TransactionFormRow.
 * Shows when user clicks Edit from the row menu. Lets user attach the statement to a specific bill (move to bill).
 */
const EditDefaultStatementRow = ({ statement, onClose, onSuccess }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const [targetBillId, setTargetBillId] = useState('');
    const [submitError, setSubmitError] = useState(null);

    const { data: bills = [] } = useQuery(
        ['templates', 'all'],
        () => listUsersTemplates(csrfToken, 'all'),
        { enabled: !!csrfToken }
    );
    const targetBills = useMemo(() => {
        const filtered = (bills || []).filter((b) => b && b.id && !b.is_default);
        return sortBillsLikeBillBar(filtered, authClaims);
    }, [bills, authClaims]);

    const mutation = useMutation(
        () => moveDefaultStatementToBill(csrfToken, statement.id, targetBillId),
        {
            onSuccess: () => {
                setSubmitError(null);
                queryClient.invalidateQueries(['defaultBill']);
                queryClient.invalidateQueries(['dashboard']);
                onSuccess?.();
                onClose();
            },
            onError: (err) => {
                const msg = err.response?.data?.msg || err.response?.data?.error || err.message || 'Failed to move statement';
                setSubmitError(msg);
            },
        }
    );

    const canSubmit = !!targetBillId && !mutation.isLoading;

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        if (!canSubmit) return;
        mutation.mutate();
    };

    return (
        <>
            {/* Header Row */}
            <TableRow>
                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }} />
                <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                    <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                        Edit statement
                    </Typography>
                </TableCell>
                <TableCell sx={{ width: '56px !important', minWidth: '56px !important', maxWidth: '56px !important', p: 0, flexShrink: 0, flexGrow: 0, borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{ color: 'inherit', padding: '4px', '&:hover': { backgroundColor: theme.colors.overlayHover } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>
            {/* Form Row */}
            <TableRow>
                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 2 }}>
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme.colors.text,
                                    '& fieldset': { borderColor: theme.colors.secondary || theme.colors.brandSecondary },
                                    '&:hover fieldset': { borderColor: theme.colors.text },
                                    '&.Mui-focused fieldset': { borderColor: theme.colors.text },
                                },
                                '& .MuiInputLabel-root': { color: theme.colors.secondary || theme.colors.brandSecondary },
                                '&.Mui-focused .MuiInputLabel-root': { color: theme.colors.text },
                            }}
                        >
                            <InputLabel id="edit-statement-bill-label">Attach to bill</InputLabel>
                            <Select
                                labelId="edit-statement-bill-label"
                                value={targetBillId}
                                label="Attach to bill"
                                onChange={(e) => setTargetBillId(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme.colors?.backgroundPrimary,
                                            color: theme.colors?.text,
                                            maxHeight: 320,
                                        },
                                    },
                                }}
                            >
                                {targetBills.map((bill) => (
                                    <MenuItem key={bill.id} value={bill.id} sx={{ color: theme.colors?.text }}>
                                        {bill.name || bill.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {submitError && (
                            <Alert severity="error" onClose={() => setSubmitError(null)}>
                                {submitError}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="outlined"
                                fullWidth
                                sx={{
                                    border: `1px solid ${theme.colors.border || '#ccc'}`,
                                    backgroundColor: 'transparent',
                                    color: theme.colors.text,
                                    '&:hover': { borderColor: theme.colors.border || '#ccc', backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)' },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="outlined"
                                fullWidth
                                disabled={!canSubmit}
                                sx={{
                                    border: `1px solid ${canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc')}`,
                                    backgroundColor: 'transparent',
                                    color: canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : theme.colors.text,
                                    '&:hover': {
                                        borderColor: canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc'),
                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                {mutation.isLoading ? 'Moving…' : 'Move to bill'}
                            </Button>
                        </Box>
                    </Box>
                </TableCell>
                <TableCell sx={{ width: '56px !important', minWidth: '56px !important', maxWidth: '56px !important', p: 0, flexShrink: 0, flexGrow: 0 }} />
            </TableRow>
        </>
    );
};

export default EditDefaultStatementRow;
